import type { ActivityLogRow } from '@/api/admin/useActivityLogs';

interface RoutePattern {
  method?: string;
  pattern: RegExp;
  description: string;
}

const ROUTE_PATTERNS: RoutePattern[] = [
  // Auth
  { method: 'POST', pattern: /\/auth\/register$/, description: '회원가입' },
  { method: 'POST', pattern: /\/auth\/login$/, description: '로그인' },
  { method: 'POST', pattern: /\/auth\/logout$/, description: '로그아웃' },
  { method: 'GET', pattern: /\/auth\/profile$/, description: '프로필 조회' },
  { method: 'PATCH', pattern: /\/auth\/profile$/, description: '프로필 수정' },
  { method: 'POST', pattern: /\/auth\/change-password$/, description: '비밀번호 변경' },
  { method: 'POST', pattern: /\/auth\/send-invitation$/, description: '초대 발송' },
  { method: 'POST', pattern: /\/auth\/delete-account$/, description: '계정 삭제' },
  { method: 'GET', pattern: /\/auth\/users$/, description: '사용자 목록 조회' },
  { method: 'PATCH', pattern: /\/auth\/users\/\d+\/role$/, description: '사용자 역할 변경' },
  { method: 'PATCH', pattern: /\/auth\/users\/\d+\/profile$/, description: '사용자 정보 수정' },
  { method: 'POST', pattern: /\/auth\/refresh/, description: '토큰 갱신' },
  { method: 'POST', pattern: /\/auth\/request-password-reset$/, description: '비밀번호 재설정 요청' },
  { method: 'POST', pattern: /\/auth\/reset-password$/, description: '비밀번호 재설정' },
  { method: 'GET', pattern: /\/auth\/validate-invite$/, description: '초대 링크 확인' },

  // Device
  { method: 'POST', pattern: /\/device\/register$/, description: '기기 등록' },
  { method: 'GET', pattern: /\/device\/list$/, description: '기기 목록 조회' },
  { method: 'PUT', pattern: /\/device\/\d+$/, description: '기기 정보 수정' },
  { method: 'DELETE', pattern: /\/device\/\d+$/, description: '기기 삭제' },
  { method: 'POST', pattern: /\/device\/reassign$/, description: '기기 재배정' },
  { method: 'GET', pattern: /\/device\/\d+$/, description: '기기 상세 조회' },
  { method: 'GET', pattern: /\/device\/serial\//, description: '기기 시리얼 조회' },
  { method: 'GET', pattern: /\/device\/check-ownership\//, description: '기기 소유권 확인' },
  { method: 'GET', pattern: /\/device\/.*\/monitoring-data$/, description: '기기 모니터링 데이터 조회' },
  { method: 'GET', pattern: /\/device\/configuration\/at-timestamp$/, description: '기기 설정 이력 조회' },
  { method: 'POST', pattern: /\/device\/save-data$/, description: '기기 데이터 저장' },
  { method: 'POST', pattern: /\/device\/packet$/, description: '기기 패킷 전송' },

  // Site managers
  { method: 'GET', pattern: /\/site\/\d+\/managers$/, description: '사이트 매니저 조회' },
  { method: 'POST', pattern: /\/site\/\d+\/managers$/, description: '사이트 매니저 초대' },
  { method: 'DELETE', pattern: /\/site\/\d+\/managers\/\d+$/, description: '사이트 매니저 삭제' },

  // Site areas
  { method: 'GET', pattern: /\/site\/\d+\/areas$/, description: '작업 구역 목록 조회' },
  { method: 'POST', pattern: /\/site\/\d+\/areas$/, description: '작업 구역 생성' },
  { method: 'GET', pattern: /\/site\/\d+\/areas\/\d+$/, description: '작업 구역 조회' },
  { method: 'PUT', pattern: /\/site\/\d+\/areas\/\d+$/, description: '작업 구역 수정' },
  { method: 'DELETE', pattern: /\/site\/\d+\/areas\/\d+$/, description: '작업 구역 삭제' },

  // Site
  { method: 'GET', pattern: /\/site\/me\/site$/, description: '내 사이트 조회' },
  { method: 'GET', pattern: /\/site\/my-sites$/, description: '내 사이트 목록 조회' },
  { method: 'GET', pattern: /\/site\/company\/\d+$/, description: '회사별 사이트 목록 조회' },
  { method: 'POST', pattern: /\/site\/company\/\d+$/, description: '사이트 생성' },
  { method: 'PUT', pattern: /\/site\/\d+$/, description: '사이트 정보 수정' },
  { method: 'DELETE', pattern: /\/site\/\d+$/, description: '사이트 삭제' },
  { method: 'GET', pattern: /\/site\/\d+\/info$/, description: '사이트 정보 조회' },

  // Company - site managers
  { method: 'GET', pattern: /\/company\/\d+\/site-managers$/, description: '사이트 매니저 목록 조회' },
  { method: 'POST', pattern: /\/company\/\d+\/site-managers$/, description: '사이트 매니저 초대' },
  { method: 'PUT', pattern: /\/company\/\d+\/site-managers\/\d+$/, description: '사이트 매니저 정보 수정' },
  { method: 'DELETE', pattern: /\/company\/\d+\/site-managers\/\d+$/, description: '사이트 매니저 삭제' },

  // Company
  { method: 'GET', pattern: /\/company\/all$/, description: '전체 회사 조회' },
  { method: 'GET', pattern: /\/company\/me\/company$/, description: '내 회사 정보 조회' },
  { method: 'GET', pattern: /\/company\/me\/company-manager$/, description: '내 회사 매니저 조회' },
  { method: 'GET', pattern: /\/company\/\d+\/company-manager$/, description: '회사 매니저 조회' },
  { method: 'GET', pattern: /\/company\/\d+$/, description: '회사 정보 조회' },
  { method: 'PUT', pattern: /\/company\/\d+$/, description: '회사 정보 수정' },
  { method: 'DELETE', pattern: /\/company\/\d+$/, description: '회사 삭제' },

  // Admin management
  { method: 'GET', pattern: /\/admin-management$/, description: '관리자 목록 조회' },
  { method: 'GET', pattern: /\/admin-management\/\d+$/, description: '관리자 정보 조회' },
  { method: 'DELETE', pattern: /\/admin-management\/\d+$/, description: '관리자 삭제' },
  { method: 'PATCH', pattern: /\/admin-management\/promote-to-admin$/, description: '관리자 승격' },

  // Allowed emails
  { method: 'POST', pattern: /\/allowed-emails$/, description: '이메일 허용 등록' },
  { method: 'GET', pattern: /\/allowed-emails$/, description: '이메일 허용 목록 조회' },
  { method: 'PATCH', pattern: /\/allowed-emails\/\d+\/status$/, description: '가입 요청 승인/거부' },
  { method: 'PATCH', pattern: /\/allowed-emails\/\d+$/, description: '이메일 허용 수정' },
  { method: 'DELETE', pattern: /\/allowed-emails\/\d+$/, description: '이메일 허용 삭제' },
  { method: 'GET', pattern: /\/allowed-emails\/check\//, description: '이메일 허용 확인' },

  // Dashboard
  { method: 'GET', pattern: /\/dashboard\/static$/, description: '대시보드 조회' },
  { method: 'GET', pattern: /\/dashboard\/alarm-history$/, description: '알람 이력 조회' },
  { method: 'GET', pattern: /\/dashboard\/live-aggregation$/, description: '실시간 집계 조회' },

  // Monitoring
  { method: 'GET', pattern: /\/monitoring\/latest$/, description: '모니터링 최신 데이터 조회' },
  { method: 'POST', pattern: /\/monitoring\/history$/, description: '모니터링 이력 조회' },
  { method: 'GET', pattern: /\/monitoring\/available-dates$/, description: '모니터링 가용 날짜 조회' },
  { method: 'GET', pattern: /\/monitoring\/push-history$/, description: '푸시 이력 조회' },

  // FCM
  { method: 'POST', pattern: /\/fcm\/token$/, description: 'FCM 토큰 등록' },
  { method: 'POST', pattern: /\/fcm\/web-token$/, description: '웹 푸시 토큰 등록' },
  { method: 'DELETE', pattern: /\/fcm\/web-token/, description: '웹 푸시 토큰 삭제' },
  { method: 'POST', pattern: /\/fcm\/test-push$/, description: '테스트 푸시 발송' },
  { method: 'GET', pattern: /\/fcm\/tokens\//, description: 'FCM 토큰 조회' },
  { method: 'DELETE', pattern: /\/fcm\/tokens\//, description: 'FCM 토큰 삭제' },

  // BellConnect
  { method: 'POST', pattern: /\/bellconnect\/packet$/, description: '벨커넥트 패킷 전송' },
  { method: 'DELETE', pattern: /\/bellconnect\/connection\//, description: '벨커넥트 연결 해제' },

  // WebSocket admin
  { method: 'GET', pattern: /\/websocket\/connections$/, description: 'WebSocket 연결 목록 조회' },
  { method: 'GET', pattern: /\/websocket\/stats$/, description: 'WebSocket 통계 조회' },
  { method: 'DELETE', pattern: /\/websocket\/connections\//, description: 'WebSocket 연결 종료' },
  { method: 'DELETE', pattern: /\/websocket\/devices\//, description: 'WebSocket 기기 연결 종료' },
  { method: 'DELETE', pattern: /\/websocket\/sites\//, description: 'WebSocket 웹 클라이언트 종료' },

  // Lite/Mini log (device data sync)
  { method: 'POST', pattern: /\/(lite-log|mini-log)\/request$/, description: '기기 로그 업로드 요청' },
  { method: 'POST', pattern: /\/(lite-log|mini-log)\/chunk/, description: '기기 로그 데이터 전송' },
  { method: 'POST', pattern: /\/(lite-log|mini-log)\/\d+\/complete$/, description: '기기 로그 업로드 완료' },
  { method: 'POST', pattern: /\/(lite-log|mini-log)\/\d+\/parse$/, description: '기기 로그 파싱' },
  { method: 'POST', pattern: /\/(lite-log|mini-log)\/parse\/batch$/, description: '기기 로그 일괄 파싱' },
  { method: 'GET', pattern: /\/(lite-log|mini-log)\/gas-readings/, description: '가스 측정 데이터 조회' },
  { method: 'GET', pattern: /\/(lite-log|mini-log)\/calibration-logs/, description: '캘리브레이션 로그 조회' },
  { method: 'GET', pattern: /\/(lite-log|mini-log)\/gas-alarm-logs/, description: '가스 알람 로그 조회' },
  { method: 'GET', pattern: /\/(lite-log|mini-log)\/event-logs/, description: '이벤트 로그 조회' },
  { method: 'GET', pattern: /\/(lite-log|mini-log)\/configuration-logs/, description: '설정 로그 조회' },
  { method: 'GET', pattern: /\/(lite-log|mini-log)\/available-dates/, description: '가용 날짜 조회' },

  // Email verification
  { method: 'POST', pattern: /\/email-verification\/send-link$/, description: '이메일 인증 링크 발송' },
  { method: 'POST', pattern: /\/email-verification\/verify/, description: '이메일 인증' },
  { method: 'GET', pattern: /\/email-verification\/check-status$/, description: '이메일 인증 상태 확인' },
  { method: 'GET', pattern: /\/email-verification\/registration-status$/, description: '가입 상태 확인' },

  // Activity logs
  { method: 'GET', pattern: /\/activity-logs$/, description: '활동 로그 조회' },

  // Unified (device packet)
  { method: 'POST', pattern: /\/unified\/packet/, description: '통합 패킷 전송' },
];

const FEATURE_ACTION_MAP: Record<string, Record<string, string>> = {
  auth: { view: '인증 정보 조회', create: '인증 요청', update: '인증 정보 수정', delete: '인증 정보 삭제' },
  company: { view: '회사 정보 조회', create: '회사 생성', update: '회사 정보 수정', delete: '회사 삭제' },
  site: { view: '사이트 조회', create: '사이트 생성', update: '사이트 수정', delete: '사이트 삭제' },
  device: { view: '기기 조회', create: '기기 등록', update: '기기 수정', delete: '기기 삭제' },
  admin: { view: '관리자 조회', create: '관리자 생성', update: '관리자 수정', delete: '관리자 삭제' },
  dashboard: { view: '대시보드 조회', create: '대시보드 데이터 생성', update: '대시보드 수정', delete: '대시보드 삭제' },
  bellconnect: { view: '벨커넥트 조회', create: '벨커넥트 연결', update: '벨커넥트 수정', delete: '벨커넥트 연결 해제' },
  monitoring: { view: '모니터링 조회', create: '모니터링 데이터 생성', update: '모니터링 수정', delete: '모니터링 삭제' },
  fcm: { view: 'FCM 조회', create: 'FCM 토큰 등록', update: 'FCM 수정', delete: 'FCM 토큰 삭제' },
  health: { view: '헬스체크', create: '헬스체크', update: '헬스체크', delete: '헬스체크' },
  websocket: { view: 'WebSocket 조회', create: 'WebSocket 연결', update: 'WebSocket 수정', delete: 'WebSocket 연결 종료' },
};

const ACTION_LABEL_MAP: Record<number, string> = {
  0: 'other',
  1: 'view',
  2: 'create',
  3: 'update',
  4: 'delete',
};

export function describeActivity(row: ActivityLogRow): string {
  const routePath = (row.routePath ?? '').split('?')[0];
  const method = (row.httpMethod ?? '').toUpperCase();

  for (const rp of ROUTE_PATTERNS) {
    if (rp.method && rp.method !== method) continue;
    if (rp.pattern.test(routePath)) {
      return rp.description;
    }
  }

  const featureLabel = (row.featureLabel ?? '').toLowerCase();
  const actionKey = ACTION_LABEL_MAP[row.action] ?? 'other';
  const featureMap = FEATURE_ACTION_MAP[featureLabel];
  if (featureMap) {
    return featureMap[actionKey] ?? `${featureLabel} (${actionKey})`;
  }

  if (featureLabel && actionKey !== 'other') {
    return `${featureLabel} ${actionKey}`;
  }

  return '—';
}
